import { useState } from "react";
import { Link } from "react-router-dom";
import MemberPhotoPlaceholder from "@/components/base/MemberPhotoPlaceholder";
import { normalizePhotoPosition, PHOTO_POSITION_CLASS } from "@/lib/avatar";

interface Member {
  id: string;
  name: string;
  profession: string;
  description: string;
  photo: string;
  photo_position?: string;
  whatsapp: string | null;
  email: string;
  instagram?: string;
  city_state?: string;
  website?: string;
  services?: string;
}

interface MemberGridProps {
  members: Member[];
}

export default function MemberGrid({ members }: MemberGridProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
      {members.map((member) => {
        const isExpanded = expandedId === member.id;
        return (
          <div
            key={member.id}
            className="group bg-white rounded-2xl overflow-hidden transition-all duration-500 hover:shadow-md shadow-sm"
          >
            {/* Photo - full width large */}
            <div className="aspect-[4/5] overflow-hidden relative">
              {member.photo ? (
                <img
                  src={member.photo}
                  alt={member.name}
                  className={`w-full h-full object-cover ${PHOTO_POSITION_CLASS[normalizePhotoPosition(member.photo_position)]} transition-transform duration-700 group-hover:scale-105`}
                />
              ) : (
                <MemberPhotoPlaceholder size="xl" rounded="none" name={member.name} />
              )}
              {/* Colonial ribbon on photo */}
              <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-primary/60 via-primary/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4 pt-12">
                <h3 className="font-display text-lg text-white mb-0.5">
                  {member.name}
                </h3>
                <p className="font-body text-xs text-white/80 tracking-wide uppercase">
                  {member.profession}
                </p>
              </div>
            </div>

            <div className="p-5">
              {member.city_state && (
                <p className="font-body text-xs text-brown/50 mb-2 flex items-center gap-1">
                  <i className="ri-map-pin-line" />
                  {member.city_state}
                </p>
              )}
              <p className="font-body text-sm text-brown/70 leading-relaxed mb-4 line-clamp-3">
                {member.description}
              </p>

              {/* Ver mais -> Profile page */}
              <Link
                to={`/perfil/${member.id}`}
                className="inline-block font-body text-xs text-primary hover:text-primary-dark underline transition-colors mb-3 whitespace-nowrap"
              >
                Ver perfil completo <i className="ri-arrow-right-line ml-1 text-[10px]" />
              </Link>

              {/* Expand for quick contact */}
              <button
                onClick={() => setExpandedId(isExpanded ? null : member.id)}
                className="block w-full text-left font-body text-xs text-brown/50 hover:text-brown transition-colors mb-3"
              >
                {isExpanded ? "Ocultar contatos" : "Ver contatos rápidos"}
              </button>

              {isExpanded && (
                <div className="border-t border-nude pt-3 space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                  {member.website && (
                    <a
                      href={member.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 font-body text-xs text-brown hover:text-primary transition-colors"
                    >
                      <i className="ri-global-line" />
                      {member.website.replace(/^https?:\/\//, "")}
                    </a>
                  )}
                  {member.instagram && (
                    <a
                      href={`https://instagram.com/${member.instagram.replace("@", "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 font-body text-xs text-brown hover:text-primary transition-colors"
                    >
                      <i className="ri-instagram-line" />
                      {member.instagram}
                    </a>
                  )}
                  {member.email && (
                    <a
                      href={`mailto:${member.email}`}
                      className="flex items-center gap-2 font-body text-xs text-brown hover:text-primary transition-colors"
                    >
                      <i className="ri-mail-line" />
                      {member.email}
                    </a>
                  )}
                </div>
              )}

              {/* Action row */}
              {(member.whatsapp || member.email) && (
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-nude">
                  <div className="flex items-center gap-2">
                    {member.whatsapp && (
                      <a
                        href={`https://wa.me/${member.whatsapp}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-9 h-9 flex items-center justify-center rounded-full bg-beige text-brown hover:bg-primary hover:text-white transition-colors duration-300"
                        title="WhatsApp"
                      >
                        <i className="ri-whatsapp-line text-sm" />
                      </a>
                    )}
                    {member.email && (
                      <a
                        href={`mailto:${member.email}`}
                        className="w-9 h-9 flex items-center justify-center rounded-full bg-beige text-brown hover:bg-primary hover:text-white transition-colors duration-300"
                        title="Email"
                      >
                        <i className="ri-mail-line text-sm" />
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}